<template>
  <v-form style="width: 100%;" ref="form" v-model="isFormValid">
    <v-container style="width: 100%;" grid-list-md pa-5>
      <v-layout mb-2 wrap>
        <v-text-field v-if="isUpdating" label="Service Id (GUID)" disabled :rules="[rules.required, rules.empty, rules.guid]" v-model="serviceObject.Id"></v-text-field>
      </v-layout>
      <v-layout mb-2 wrap>
        <v-text-field :disabled="isValidating" :rules="[rules.required, rules.empty]" placeholder="http://www.xyz.com" v-model="serviceObject.BaseUrl">
          <template #label>
            Base Url
            <RequiredStar />
          </template>
        </v-text-field>
      </v-layout>
      <v-text-field label="Display Name" :disabled="isValidating" :placeholder="displayNamePlaceholder" persistent-hint v-model="serviceObject.DisplayName" />
      <v-text-field label="Capabilities URL" :disabled="isValidating" :placeholder="capabilitiesPlaceholder" v-model="serviceObject.CapabilitiesUrl" persistent-hint />
      <v-select label="Service Link" :disabled="isValidating" :items="serviceLinks" item-text="ServiceLink" item-value="Id" v-model="serviceObject.LinkedServiceId" persistent-hint />

      <!-- <div v-if="validationCapabilities" style="margin-bottom: 40px;">
        <h3 style="color: red"> Service claims </h3>
        <p> This service will be allowed the following claims: </p>
        <ul>
          <li v-for="(claim, index) in validationCapabilities.ServiceClaims" :key="index">
            <strong>[Tenant Admin]</strong> {{ claim }} 
          </li>
        </ul>
      </div> -->
      <v-layout wrap mt-5>
        <v-btn :loading="isValidating" @click="validate()" style="margin: 0 auto;">{{$t('Button.ValidateService')}}</v-btn>
      </v-layout>
      <v-layout wrap>
        <v-chip :color="labelColor" text-color="white" style="margin: 0 auto; margin-top: 15px;">
          <v-avatar>
            <v-icon v-if="isValidated">check_circle</v-icon>
            <v-icon v-else>error_outline</v-icon>
          </v-avatar>
          {{ labelMessage }}
        </v-chip>
      </v-layout>
      <v-layout wrap>
        <p style="margin: 0 auto; margin-top: 10px;"> {{ validationMessage }} </p>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
  const GuidRegex = new RegExp(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/)
  import { createNamespacedHelpers } from 'vuex'
  const linkedServices = createNamespacedHelpers('linkedservices')

  export default {
    props: {
      releaseTag: String,
    },
    data: () => ({
      validationCapabilities: null,
      isUpdating: false,
      isFormValid: false,
      isValidating: false,
      labelMessage: '',
      validationMessage: '',
      isValidated: false,
      attemptedValidation: false,
      serviceObject: {},
      initialObject: {
        Id: null,
        CapabilitiesUrl: '',
        ServiceLinkId: null,
      },
      rules: {
        required: value => !!value || 'Required',
        empty: value => value && value.trim().length > 0 || 'Cannot be white space',
        guid: value => GuidRegex.test(value) || 'Must be a valid GUID',
      },
    }),
    computed: {
      ...linkedServices.mapState(['serviceLinks']),
      releaseTagComputed: {
        get() {
          return this.releaseTag
        },
        set(value) {
          this.$emit('update:releaseTag', value)
        },
      },
      labelColor() {
        if (!this.attemptedValidation) {
          return 'warning'
        } else if (this.isValidated) {
          return 'success'
        } else {
          return 'error lighten-1'
        }
      },
      capabilitiesPlaceholder() {
        let baseUrl = this.serviceObject.BaseUrl || 'http://www.xyz.com'
        let capabilitiesUrl = baseUrl + '/api/capabilities'
        let trimmedUrl = capabilitiesUrl.replace(/(https?:\/\/)|(\/)+/g, '$1$2')
        return trimmedUrl
      },
      displayNamePlaceholder() {
        return this.validationCapabilities && this.validationCapabilities.ServiceName || 'A descriptive name'
      },
      submitParams() {
        return {
          ...this.serviceObject,
          CapabilitiesUrl: this.serviceObject.CapabilitiesUrl || this.capabilitiesPlaceholder,
        }
      },
    },
    watch: {
      'serviceObject.name'() {
        this.invalidate()
      },
      'serviceObject.CapabilitiesUrl'() {
        this.invalidate()
      },
      'serviceObject.dockerURL'() {
        this.invalidate()
      },
    },
    methods: {
      invalidate() {
        this.isValidating = false
        this.isValidated = false
        this.attemptedValidation = false
        this.labelMessage = 'Please validate to save'
        this.$emit('validate', false)
      },
      checkFormValid() {
        this.$refs.form.validate()
        if (!this.isFormValid) {
          this.setMessageState(false, 'Failed to validate', 'Please fix form errors')
        }

        return this.isFormValid
      },
      setBlockAndIsValidating(value) {
        this.isValidating = value
        this.$emit('block', value)
      },
      async validate() {
        this.setBlockAndIsValidating(true)

        if (this.checkFormValid()) {
          try {
            let response = await this.$store.dispatch('services/validateService', this.submitParams)
            this.validationCapabilities = response
            this.setMessageState(true, 'Successfully validated!', '')
            this.setBlockAndIsValidating(false)
            this.$emit('validate', true)
          } catch (error) {
            console.log(Object.keys(error.response))
            this.validationCapabilities = null
            this.setBlockAndIsValidating(false)
            this.setMessageState(false, 'Validation failed!', error.response.data.ExceptionMessage)
          }
        } else {
          this.setBlockAndIsValidating(false)
        }
      },
      submit() {
        this.$emit('submit', this.submitParams)
      },
      resetForm() {
        this.serviceObject = JSON.parse(JSON.stringify(this.initialObject))
        this.$refs.form.resetValidation()
        this.invalidate()
        this.validationMessage = ''
        this.validationCapabilities = null
      },
      assignObject(service) {
        this.isUpdating = true
        this.serviceObject = JSON.parse(JSON.stringify(service))
      },
      setMessageState(state, labelMessage, validationMessage) {
        this.attemptedValidation = true
        this.isValidated = state
        this.labelMessage = labelMessage
        this.validationMessage = validationMessage
      },
    },
    mounted() {
      this.resetForm()
    },
  }
</script>